import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useDatos, useSesion } from '../lib/sesion';
import { fechaHora } from '../lib/formato';
import type { Solicitud } from '../lib/tipos';
import { Aviso, Boton, Campo, Cargando, Tarjeta } from './ui';
import { SelectorFranja, type Franja } from './SelectorFranja';

interface ReviewData {
  status: string;
  version: number;
  performed: boolean | null;
  submittedAt: string | null;
  followupResult: 'YES' | 'NO' | 'NO_ANSWER' | null;
  followupCloseAt: string | null;
  review: {
    teamRating: number | null;
    clientRating: number | null;
    teamComments: string | null;
    clientComments: string | null;
    orderFeedback: string | null;
    followupComment?: string | null;
  } | null;
}
export function Valoracion({ ficha, onCambio }: { ficha: Solicitud; onCambio: () => void }) {
  const { usuario } = useSesion();
  if (
    usuario?.role === 'COOK' ||
    !['PENDING_REVIEW', 'PENDING_FOLLOWUP', 'CLOSED', 'NOT_PERFORMED'].includes(ficha.status)
  )
    return null;
  return <FormularioValoracion key={ficha.id} ficha={ficha} onCambio={onCambio} />;
}
function FormularioValoracion({ ficha, onCambio }: { ficha: Solicitud; onCambio: () => void }) {
  const { es } = useSesion();
  const privilegiado = es('SALES_MANAGER', 'ADMIN');
  const { datos, cargando, error, recargar } = useDatos(
    (s) => api.get<ReviewData>(`/requests/${ficha.id}/review`, s),
    [ficha.id, ficha.status, ficha.version],
  );
  const [performed, setPerformed] = useState<boolean | null>(null);
  const [reason, setReason] = useState('CLIENT_CANCELLED'),
    [comment, setComment] = useState('');
  const [later, setLater] = useState<boolean | null>(null),
    [seleccion, setSeleccion] = useState<Franja | null>(null);
  const [slots, setSlots] = useState<Franja[]>([]);
  const [team, setTeam] = useState<number | null>(null),
    [client, setClient] = useState<number | null>(null);
  const [teamComments, setTeamComments] = useState(''),
    [clientComments, setClientComments] = useState(''),
    [feedback, setFeedback] = useState('');
  const [followupComment, setFollowupComment] = useState(''),
    [fallo, setFallo] = useState<string | null>(null),
    [busy, setBusy] = useState(false);
  useEffect(() => {
    setTeam(datos?.review?.teamRating ?? null);
    setClient(datos?.review?.clientRating ?? null);
    setTeamComments(datos?.review?.teamComments ?? '');
    setClientComments(datos?.review?.clientComments ?? '');
    setFeedback(datos?.review?.orderFeedback ?? '');
    setFollowupComment(privilegiado ? (datos?.review?.followupComment ?? '') : '');
  }, [datos, privilegiado]);
  async function guardar(path: string, body: unknown, method: 'post' | 'put') {
    setFallo(null);
    setBusy(true);
    try {
      await api[method](`/requests/${ficha.id}/${path}`, body);
      recargar();
      onCambio();
      window.dispatchEvent(new Event('tareas-actualizadas'));
    } catch (e) {
      setFallo(e instanceof ApiError ? e.message : 'No se ha podido guardar.');
    } finally {
      setBusy(false);
    }
  }
  const rating = (label: string, value: number | null, set: (n: number) => void) => (
    <Campo etiqueta={label}>
      <div className="notas-valoracion" role="group" aria-label={label}>
        {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
          <Boton
            key={n}
            aria-pressed={value === n}
            variante={value === n ? 'clave' : 'secundario'}
            onClick={() => set(n)}
          >
            {n}
          </Boton>
        ))}
      </div>
    </Campo>
  );
  if (cargando) return <Cargando />;
  if (error) return <Aviso tono="error">{error}</Aviso>;
  if (!datos) return null;
  const closed = ['CLOSED', 'NOT_PERFORMED'].includes(datos.status);
  const valid =
    team !== null &&
    client !== null &&
    feedback.trim() &&
    (team > 4 || teamComments.trim()) &&
    (client > 4 || clientComments.trim());
  return (
    <Tarjeta titulo="Realización, valoración y seguimiento">
      <div className="pila">
        {fallo && <Aviso tono="error">{fallo}</Aviso>}
        {datos.status === 'PENDING_REVIEW' && datos.performed === null && (
          <>
            <h3>Paso 1 · ¿Se ha realizado finalmente?</h3>
            <div className="fila">
              <Boton aria-pressed={performed === true} onClick={() => setPerformed(true)}>
                Sí, realizada
              </Boton>
              <Boton aria-pressed={performed === false} onClick={() => setPerformed(false)}>
                No se ha realizado
              </Boton>
            </div>
            {performed === false && (
              <>
                <Campo etiqueta="Motivo">
                  <select value={reason} onChange={(e) => setReason(e.target.value)}>
                    <option value="CLIENT_CANCELLED">El cliente canceló</option>
                    <option value="CLIENT_ABSENT">El cliente no se presentó</option>
                    <option value="TEAM_UNAVAILABLE">Imposibilidad del equipo</option>
                    <option value="LOGISTICS">Logística</option>
                    <option value="FORCE_MAJEURE">Causa mayor</option>
                    <option value="OTHER">Otro</option>
                  </select>
                </Campo>
                <Campo etiqueta={reason === 'OTHER' ? 'Comentario obligatorio' : 'Comentario'}>
                  <textarea value={comment} onChange={(e) => setComment(e.target.value)} />
                </Campo>
                <p>¿Se realizará más adelante?</p>
                <div className="fila">
                  <Boton aria-pressed={later === true} onClick={() => setLater(true)}>
                    Sí, proponer nuevas fechas
                  </Boton>
                  <Boton aria-pressed={later === false} onClick={() => setLater(false)}>
                    No, definitivamente
                  </Boton>
                </div>
                {later === true && (
                  <>
                    <SelectorFranja
                      bloquearOcupadas
                      excludeRequestId={ficha.id}
                      valor={seleccion}
                      onCambio={setSeleccion}
                    />
                    <Boton
                      disabled={!seleccion}
                      onClick={() => {
                        if (seleccion) {
                          setSlots([...slots, seleccion]);
                          setSeleccion(null);
                        }
                      }}
                    >
                      Añadir nueva fecha
                    </Boton>
                    {slots.map((s, i) => (
                      <div className="fila" key={i}>
                        {s.inicio} — {s.fin}
                        <Boton onClick={() => setSlots(slots.filter((_, n) => n !== i))}>
                          Quitar fecha
                        </Boton>
                      </div>
                    ))}
                  </>
                )}
              </>
            )}
            <Boton
              variante="clave"
              cargando={busy}
              disabled={
                performed === null ||
                (performed === false &&
                  (later === null ||
                    (reason === 'OTHER' && !comment.trim()) ||
                    (later && !slots.length)))
              }
              onClick={() =>
                guardar(
                  'execution-check',
                  {
                    performed,
                    reason,
                    comment,
                    willReschedule: later ?? false,
                    slots: slots.map((s) => ({
                      start: new Date(s.inicio).toISOString(),
                      end: new Date(s.fin).toISOString(),
                    })),
                  },
                  'post',
                )
              }
            >
              Guardar realización
            </Boton>
          </>
        )}
        {datos.submittedAt && (
          <Aviso tono="hecho">Valoración enviada el {fechaHora(datos.submittedAt)}</Aviso>
        )}
        {ficha.timeline.some(
          (h) => h.action === 'REVIEW_EDITED' && h.actorRole === 'SALES_MANAGER',
        ) &&
          privilegiado && <p>Editado por Jefe de Ventas</p>}
        {((datos.performed === true && !datos.submittedAt) ||
          (datos.submittedAt && privilegiado)) && (
          <>
            <h3>Paso 2 · Valoración</h3>
            {rating('Comportamiento del equipo', team, setTeam)}
            <Campo
              etiqueta={
                team !== null && team <= 4
                  ? 'Comentarios sobre el equipo (obligatorios)'
                  : 'Comentarios sobre el equipo'
              }
            >
              <textarea value={teamComments} onChange={(e) => setTeamComments(e.target.value)} />
            </Campo>
            {rating('Comportamiento del cliente', client, setClient)}
            <Campo
              etiqueta={
                client !== null && client <= 4
                  ? 'Comentarios sobre el cliente (obligatorios)'
                  : 'Comentarios sobre el cliente'
              }
            >
              <textarea
                value={clientComments}
                onChange={(e) => setClientComments(e.target.value)}
              />
            </Campo>
            <Campo etiqueta="Feedback del pedido">
              <textarea value={feedback} onChange={(e) => setFeedback(e.target.value)} />
            </Campo>
            <div className="fila">
              {!datos.submittedAt && (
                <Boton
                  cargando={busy}
                  onClick={() =>
                    guardar(
                      'review/phase1',
                      {
                        teamRating: team,
                        clientRating: client,
                        teamComments,
                        clientComments,
                        orderFeedback: feedback,
                        version: datos.version,
                        submit: false,
                      },
                      'put',
                    )
                  }
                >
                  Guardar borrador
                </Boton>
              )}
              <Boton
                variante="clave"
                cargando={busy}
                disabled={!valid}
                onClick={() =>
                  guardar(
                    'review/phase1',
                    {
                      teamRating: team,
                      clientRating: client,
                      teamComments,
                      clientComments,
                      orderFeedback: feedback,
                      version: datos.version,
                      submit: true,
                    },
                    'put',
                  )
                }
              >
                {datos.submittedAt ? 'Guardar edición de valoración' : 'Enviar valoración'}
              </Boton>
            </div>
          </>
        )}
        {datos.submittedAt && (
          <>
            <h3>
              Paso 3 · ¿El cliente ha pedido algo de lo degustado o como consecuencia de la
              degustación?
            </h3>
            {datos.followupResult && (
              <p>
                Respuesta:{' '}
                {datos.followupResult === 'YES'
                  ? 'Sí, éxito'
                  : datos.followupResult === 'NO'
                    ? 'No (de momento)'
                    : 'Sin respuesta'}
              </p>
            )}
            {!closed && (
              <>
                <Campo etiqueta="Comentario opcional">
                  <textarea
                    value={followupComment}
                    onChange={(e) => setFollowupComment(e.target.value)}
                  />
                </Campo>
                <div className="fila">
                  <Boton
                    variante="clave"
                    cargando={busy}
                    onClick={() =>
                      guardar(
                        'review/phase2',
                        {
                          orderedSomething: true,
                          comment: followupComment,
                          version: datos.version,
                        },
                        'put',
                      )
                    }
                  >
                    Sí, ha pedido
                  </Boton>
                  <Boton
                    cargando={busy}
                    onClick={() =>
                      guardar(
                        'review/phase2',
                        {
                          orderedSomething: false,
                          comment: followupComment,
                          version: datos.version,
                        },
                        'put',
                      )
                    }
                  >
                    No (de momento)
                  </Boton>
                </div>
                <p className="sutil">
                  Puede cambiar No por Sí hasta el cierre automático del{' '}
                  {fechaHora(datos.followupCloseAt)}.
                </p>
              </>
            )}
          </>
        )}
        {datos.status === 'NOT_PERFORMED' && (
          <p>Visita no realizada. Puede crear una nueva propuesta.</p>
        )}
      </div>
    </Tarjeta>
  );
}
